import { useState } from 'react';
import { GitBranch } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Table, type Column } from '@/shared/components/ui/Table';
import { usePipeline } from '../hooks/useAnalytics';
import { PanelState } from './PanelState';
import {
  stageLabel,
  STAGE_TONE,
  formatCount,
  formatCurrency,
  STAGE_DETAIL_PAGE,
} from '../constants';
import type { PipelineStage, PipelineDeal, CurrencyTotal } from '../types';

/** Render each currency total separately — currencies are never summed (Req 1.3). */
function CurrencyTotals({ totals }: { totals: CurrencyTotal[] }) {
  if (!totals || totals.length === 0) {
    return <span className="text-on-surface-variant">—</span>;
  }
  return (
    <div className="flex flex-col items-end gap-0.5">
      {totals.map((t) => (
        <span key={t.currency} className="tabular-nums">
          {formatCurrency(t.total, t.currency)}
        </span>
      ))}
    </div>
  );
}

export function PipelinePanel({ enabled }: { enabled: boolean }) {
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  // Overview query (no stage) — the six-stage summary with per-currency totals.
  const overview = usePipeline({ enabled });

  // Detail query (only when a stage is selected) — bounded/paginated deal list.
  const detail = usePipeline({
    stage: selectedStage,
    limit: STAGE_DETAIL_PAGE,
    offset,
    enabled: enabled && !!selectedStage,
  });

  const stages = overview.data?.stages ?? [];

  const columns: Column<PipelineStage>[] = [
    {
      key: 'stage',
      header: 'Stage',
      render: (r) => <Badge tone={STAGE_TONE[r.stage] ?? 'neutral'}>{stageLabel(r.stage)}</Badge>,
    },
    { key: 'count', header: 'Deals', align: 'right', render: (r) => formatCount(r.count) },
    {
      key: 'totals',
      header: 'Value by currency',
      align: 'right',
      render: (r) => <CurrencyTotals totals={r.totalsByCurrency} />,
    },
  ];

  const detailDeals = detail.data?.deals ?? [];
  const dealColumns: Column<PipelineDeal>[] = [
    {
      key: 'title',
      header: 'Deal',
      render: (d) => (
        <span className="font-medium text-on-surface">{d.title || d.company_name || d.id}</span>
      ),
    },
    { key: 'company', header: 'Company', render: (d) => d.company_name || '—' },
    {
      key: 'value',
      header: 'Value',
      align: 'right',
      render: (d) =>
        d.value != null ? formatCurrency(d.value, d.currency || 'INR') : <span className="text-on-surface-variant">—</span>,
    },
  ];

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <GitBranch className="h-4 w-4" /> Deal Pipeline
          </span>
        }
        subtitle="Deals per stage with value grouped by currency (currencies are never combined)."
      />
      <CardBody className="space-y-4">
        <PanelState
          isLoading={overview.isLoading}
          isError={overview.isError}
          isEmpty={!!overview.data?.empty}
          emptyTitle="No deals in scope"
          emptyDescription="No permitted deals exist yet, so there is nothing to summarize."
          emptyIcon={<GitBranch className="h-6 w-6" />}
        >
          <Table
            columns={columns}
            data={stages}
            rowKey={(r) => r.stage}
            onRowClick={(r) => {
              setOffset(0);
              setSelectedStage((cur) => (cur === r.stage ? null : r.stage));
            }}
            empty="No stages to show."
          />

          {selectedStage && (
            <div className="space-y-3 rounded-card border border-outline p-4">
              <div className="flex items-center justify-between">
                <h4 className="text-data-md font-semibold text-on-surface">
                  {stageLabel(selectedStage)} — deals
                </h4>
                <Button variant="ghost" onClick={() => setSelectedStage(null)}>
                  Close
                </Button>
              </div>
              <PanelState
                isLoading={detail.isLoading}
                isError={detail.isError}
                isEmpty={detailDeals.length === 0}
                emptyTitle="No deals in this stage"
                emptyDescription="This stage has no permitted deals for the current scope."
              >
                <Table
                  columns={dealColumns}
                  data={detailDeals}
                  rowKey={(d) => String(d.id)}
                  empty="No deals in this stage."
                />
                <div className="flex items-center justify-between pt-1">
                  <span className="text-body-sm text-on-surface-variant">
                    Showing {detailDeals.length ? offset + 1 : 0}–{offset + detailDeals.length}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      disabled={offset === 0}
                      onClick={() => setOffset((o) => Math.max(0, o - STAGE_DETAIL_PAGE))}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      // A full page implies there may be more; the server bounds it.
                      disabled={detailDeals.length < STAGE_DETAIL_PAGE}
                      onClick={() => setOffset((o) => o + STAGE_DETAIL_PAGE)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </PanelState>
            </div>
          )}
        </PanelState>
      </CardBody>
    </Card>
  );
}
